import { createContext, useContext } from 'react';
import type {
  WorkspaceBusEventType,
  WorkspaceEventHandler,
  WorkspaceStreamState,
  IntentSignal,
} from '@/shared/hooks/useWorkspaceEvents';
import type { WorkspaceTask } from '@/shared/data/workspaces';
import { predictIntentLocally } from '@/shared/hooks/useIntentSignal';

export interface AgentEventsContextValue {
  // Existing workspace SSE bus methods & state
  subscribe: (type: WorkspaceBusEventType, handler: WorkspaceEventHandler) => () => void;
  streamState: WorkspaceStreamState;
  intentSignal: IntentSignal | null;
  isAgentThinking: boolean;
  hasRunningTask: (tasks: WorkspaceTask[]) => boolean;
  isRecentSignal: (signal: IntentSignal | null, windowMs?: number) => boolean;
  predictIntentLocally: typeof predictIntentLocally;

  // Realtime Agent Session selectors (TASK-ASM-006)
  activeSessionsCount: number; // running sessions in workspace
  pendingApprovalCount: number; // pending approvals
  isBudgetExceeded: boolean; // budget exceeded flag
  workspaceEventStream: 'connected' | 'disconnected' | 'error';
}

export const defaultAgentEventsContextValue: AgentEventsContextValue = {
  subscribe: () => () => {},
  streamState: 'closed',
  intentSignal: null,
  isAgentThinking: false,
  hasRunningTask: () => false,
  isRecentSignal: () => false,
  predictIntentLocally,
  activeSessionsCount: 0,
  pendingApprovalCount: 0,
  isBudgetExceeded: false,
  workspaceEventStream: 'disconnected',
};

export const AgentEventsContext = createContext<AgentEventsContextValue>(
  defaultAgentEventsContextValue
);

export function useAgentEvents(): AgentEventsContextValue {
  return useContext(AgentEventsContext);
}
